/**
 * Lux Pond Visualizer - Luxury calm pond with ripples, caustics, petals, mist & reflections
 *
 * A "calm maximalism" audio visualizer: visually rich and layered but sleep-safe
 * (slow motion, no strobes, no harsh white blasts). Dozens of concurrent elements
 * create an immersive pond scene with parallax depth.
 *
 * Rendering layers (bottom to top):
 * 1. Trail fade — offscreen canvas composited at reduced opacity over fresh background
 * 2. Deep water gradient + vignette — radial depth with parallax color shift
 * 3. Caustics — soft polygon/arc patches that slowly morph via control points
 * 4. Ripple field — background ripple texture + audio event ripples (ring systems)
 * 5. Mist layers — translucent fog blobs drifting horizontally (near-plane parallax)
 * 6. Petals/leaves — curved shapes with gentle rotation and drift
 * 7. Reflection specks — tiny light points in bands with slow twinkle
 *
 * Audio mapping:
 * - Bass gently increases ripple amplitude and caustic contrast (clamped)
 * - Mids influences petal drift speed and number of event ripples
 * - Treble controls reflection speck density and shimmer (still calm)
 * - Energy rise detector spawns additional ripple clusters — no strobes
 *
 * Overrides draw() with offscreen canvas trail technique that respects the
 * global background controller (solid, gradient, pattern, or image backgrounds).
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'lux-pond',
  displayName: 'Lux Pond',
  contextType: '2d',
};

// --- Pool size constants ---
const MAX_RIPPLES = 80;
const MAX_CAUSTIC_PATCHES = 30;
const MAX_PETALS = 60;
const MAX_MIST_BLOBS = 20;
const MAX_REFLECTIONS = 600;

// --- State constants ---
const DEAD = 0;
const ALIVE = 1;

// --- Math constants ---
const TAU = Math.PI * 2;

// --- Palette definitions (muted, harmonious) ---
const PALETTES = [
  // Deep ocean
  ['#1a3a4a', '#2a5a6a', '#3a7a8a', '#4a9aaa', '#5abaca', '#6adaea'],
  // Twilight rose
  ['#3a2040', '#5a3060', '#7a4080', '#9a60a0', '#ba80c0', '#da90d0'],
  // Forest pool
  ['#1a2a1a', '#2a4a2a', '#3a6a3a', '#4a8a4a', '#5aaa5a', '#6aca6a'],
  // Warm amber
  ['#3a2a1a', '#5a4a2a', '#7a6a3a', '#9a8a4a', '#baaa5a', '#daca6a'],
  // Cool sapphire
  ['#1a1a3a', '#2a2a5a', '#3a3a7a', '#4a4a9a', '#5a5aba', '#6a6ada'],
  // Coral reef
  ['#3a1a2a', '#5a2a3a', '#7a3a4a', '#9a4a5a', '#ba5a6a', '#da6a7a'],
];

class LuxPond extends Base2DVisualizer {
  static name = 'lux-pond';
  static displayName = 'Lux Pond';

  static getConfigUI() {
    return [
      // Ripple controls
      { type: 'range', id: 'baseRippleDensity', label: 'Background Ripples', min: 5, max: 40, default: 15 },
      { type: 'range', id: 'eventRippleRate', label: 'Event Ripple Rate', min: 1, max: 20, default: 8 },
      { type: 'range', id: 'rippleLife', label: 'Ripple Life', min: 30, max: 300, default: 120 },
      { type: 'range', id: 'rippleThickness', label: 'Ripple Thickness', min: 1, max: 6, default: 2 },

      // Caustic controls
      { type: 'range', id: 'causticPatchCount', label: 'Caustic Patches', min: 4, max: 30, default: 15 },
      { type: 'range', id: 'causticMorphSpeed', label: 'Caustic Morph Speed', min: 1, max: 20, default: 5, scale: 0.01 },
      { type: 'range', id: 'causticContrast', label: 'Caustic Contrast', min: 1, max: 50, default: 25, scale: 0.01 },

      // Petal controls
      { type: 'range', id: 'petalCount', label: 'Petal Count', min: 5, max: 60, default: 25 },
      { type: 'range', id: 'petalSize', label: 'Petal Size', min: 3, max: 25, default: 10 },
      { type: 'range', id: 'driftSpeed', label: 'Drift Speed', min: 1, max: 20, default: 8, scale: 0.1 },
      { type: 'range', id: 'mistDensity', label: 'Mist Density', min: 2, max: 20, default: 8 },

      // Reflection controls
      { type: 'range', id: 'reflectionCount', label: 'Reflection Specks', min: 50, max: 600, default: 300 },
      { type: 'range', id: 'twinkleStrength', label: 'Twinkle Strength', min: 1, max: 50, default: 25, scale: 0.01 },
      { type: 'range', id: 'paletteRichness', label: 'Palette Richness', min: 1, max: 6, default: 3 },

      // Atmosphere controls
      { type: 'range', id: 'calmness', label: 'Calmness', min: 10, max: 100, default: 70, scale: 0.01 },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 1, max: 20, default: 4, scale: 0.01 },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 40, default: 15 },
      { type: 'range', id: 'highlightCeiling', label: 'Highlight Ceiling', min: 20, max: 100, default: 70, scale: 0.01 },
      { type: 'checkbox', id: 'safeMode', label: 'Safe Mode (lower brightness)', default: false },

      // Standard audio
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 75, scale: 0.01 },
      { type: 'color', id: 'colorStart', label: 'Color Start', default: '#1a4a6a' },
      { type: 'color', id: 'colorEnd', label: 'Color End', default: '#6a2a5a' },
    ];
  }

  getDefaultConfig() {
    return {};
  }

  getElementCount() {
    return 128;
  }

  init() {
    this.time = 0;
    this.isFirstFrame = true;

    // Trail offscreen canvas
    this._trailCanvas = null;
    this._trailCtx = null;

    // Vignette cache
    this._vignetteGrad = null;
    this._vigW = 0;
    this._vigH = 0;

    // Parse color caches
    this._csR = 0; this._csG = 0; this._csB = 0;
    this._ceR = 0; this._ceG = 0; this._ceB = 0;
    this._cachedCS = '';
    this._cachedCE = '';
    this._ensureColorCache(true);

    // Energy rise detector (integrator for spawning ripple clusters)
    this._energyIntegrator = 0;
    this._energyThreshold = 0.4;
    this._lastEnergy = 0;

    // Initialize all pools
    this._initRipplePool();
    this._initCausticPool();
    this._initPetalPool();
    this._initMistPool();
    this._initReflectionPool();

    // Active palette indices (rotated slowly)
    this._activePalettes = [];
    this._paletteHueOffset = 0;
    this._selectPalettes();

    // Pre-allocated temp arrays to avoid per-frame allocation
    this._bandEnergies = new Float64Array(4); // sub-bass, bass, mid, high
  }

  onConfigChange(changed) {
    if (changed.colorStart !== undefined || changed.colorEnd !== undefined) {
      this._ensureColorCache(true);
    }
    if (changed.paletteRichness !== undefined) {
      this._selectPalettes();
    }
    if (changed.causticPatchCount !== undefined) {
      this._initCausticPool();
    }
  }

  // ═══════════════════════════════════════════════
  // Color utilities
  // ═══════════════════════════════════════════════

  _ensureColorCache(force = false) {
    const cs = this.config.colorStart || '#1a4a6a';
    const ce = this.config.colorEnd || '#6a2a5a';
    if (force || this._cachedCS !== cs) {
      this._csR = parseInt(cs.slice(1, 3), 16) || 0;
      this._csG = parseInt(cs.slice(3, 5), 16) || 0;
      this._csB = parseInt(cs.slice(5, 7), 16) || 0;
      this._cachedCS = cs;
    }
    if (force || this._cachedCE !== ce) {
      this._ceR = parseInt(ce.slice(1, 3), 16) || 0;
      this._ceG = parseInt(ce.slice(3, 5), 16) || 0;
      this._ceB = parseInt(ce.slice(5, 7), 16) || 0;
      this._cachedCE = ce;
    }
  }

  _selectPalettes() {
    const count = Math.min(this.config.paletteRichness, PALETTES.length);
    this._activePalettes = [];
    // Evenly space palette picks across the palette array
    for (let i = 0; i < count; i++) {
      const idx = Math.floor((i / count) * PALETTES.length) % PALETTES.length;
      this._activePalettes.push(idx);
    }
  }

  /**
   * Get a color from the active palettes with slow hue drift.
   * @param {number} t - 0-1 interpolation across palette spectrum
   * @param {number} alpha - alpha channel 0-1
   * @returns {string} rgba color string
   */
  _getPaletteColor(t, alpha) {
    const palettes = this._activePalettes;
    if (palettes.length === 0) return `rgba(100,150,200,${alpha})`;

    // Map t to palette index and color index
    const totalColors = palettes.length * 6;
    const colorIdx = ((t * totalColors + this._paletteHueOffset) % totalColors + totalColors) % totalColors;
    const palIdx = palettes[Math.floor(colorIdx / 6) % palettes.length];
    const localIdx = Math.floor(colorIdx % 6);
    const hex = PALETTES[palIdx][localIdx];

    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  /**
   * Interpolate between colorStart and colorEnd.
   */
  _lerpMainColor(t, alpha) {
    const r = Math.round(this._csR + (this._ceR - this._csR) * t);
    const g = Math.round(this._csG + (this._ceG - this._csG) * t);
    const b = Math.round(this._csB + (this._ceB - this._csB) * t);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  // ═══════════════════════════════════════════════
  // Pool Initialization
  // ═══════════════════════════════════════════════

  _initRipplePool() {
    this._ripples = new Array(MAX_RIPPLES);
    this._rippleFreeList = new Array(MAX_RIPPLES);
    this._rippleFreeCount = MAX_RIPPLES;
    for (let i = 0; i < MAX_RIPPLES; i++) {
      this._ripples[i] = {
        state: DEAD,
        x: 0, y: 0,
        radius: 0, maxRadius: 0,
        life: 0, maxLife: 0,
        thickness: 1,
        alpha: 0,
        speed: 0,
        isBackground: false,
        depth: 0, // 0=far, 1=near for parallax
      };
      this._rippleFreeList[i] = i;
    }
    // Spawn initial background ripples
    this._spawnBackgroundRipples();
  }

  _initCausticPool() {
    const count = Math.min(this.config.causticPatchCount, MAX_CAUSTIC_PATCHES);
    this._caustics = new Array(MAX_CAUSTIC_PATCHES);
    for (let i = 0; i < MAX_CAUSTIC_PATCHES; i++) {
      // Each caustic has 5-7 control points that morph slowly
      const numPts = 5 + Math.floor(Math.random() * 3);
      this._caustics[i] = {
        state: i < count ? ALIVE : DEAD,
        cx: Math.random(),  // center x (0-1 fraction)
        cy: Math.random(),  // center y (0-1 fraction)
        radius: 0.04 + Math.random() * 0.08,
        numPts: numPts,
        // Control point offsets (polar: angle + radius deviation)
        angles: new Float64Array(numPts),
        radii: new Float64Array(numPts),
        targetAngles: new Float64Array(numPts),
        targetRadii: new Float64Array(numPts),
        phase: Math.random() * TAU,
        alpha: 0.03 + Math.random() * 0.05,
        depth: Math.random(), // parallax depth
        morphTimer: 0,
        morphInterval: 2 + Math.random() * 4,
        hueT: Math.random(), // palette position
      };
      // Initialize control points
      const c = this._caustics[i];
      for (let j = 0; j < numPts; j++) {
        const baseAngle = (j / numPts) * TAU;
        c.angles[j] = baseAngle;
        c.radii[j] = 0.7 + Math.random() * 0.6;
        c.targetAngles[j] = baseAngle + (Math.random() - 0.5) * 0.5;
        c.targetRadii[j] = 0.7 + Math.random() * 0.6;
      }
    }
  }

  _initPetalPool() {
    this._petals = new Array(MAX_PETALS);
    for (let i = 0; i < MAX_PETALS; i++) {
      this._petals[i] = {
        state: DEAD,
        x: 0, y: 0,
        rotation: 0,
        rotationSpeed: 0,
        vx: 0, vy: 0,
        size: 0,
        life: 0, maxLife: 0,
        alpha: 0,
        hueT: 0,
        depth: 0, // parallax depth
        petalType: 0, // 0=round, 1=elongated, 2=leaf
      };
    }
    this._petalNextIdx = 0;
  }

  _initMistPool() {
    this._mistBlobs = new Array(MAX_MIST_BLOBS);
    for (let i = 0; i < MAX_MIST_BLOBS; i++) {
      this._mistBlobs[i] = {
        state: DEAD,
        x: 0, y: 0,
        width: 0, height: 0,
        vx: 0,
        alpha: 0,
        maxAlpha: 0,
        life: 0, maxLife: 0,
        depth: 0,
      };
    }
    this._mistNextIdx = 0;
    this._mistSpawnTimer = 0;
  }

  _initReflectionPool() {
    this._reflections = new Array(MAX_REFLECTIONS);
    for (let i = 0; i < MAX_REFLECTIONS; i++) {
      this._reflections[i] = {
        state: DEAD,
        x: 0, y: 0,
        size: 0,
        alpha: 0,
        twinklePhase: 0,
        twinkleSpeed: 0,
        bandY: 0,
        depth: 0,
        hueT: 0,
      };
    }
    this._reflectionNextIdx = 0;
  }

  _spawnBackgroundRipples() {
    const count = Math.min(this.config.baseRippleDensity, MAX_RIPPLES - 10);
    for (let i = 0; i < count; i++) {
      this._spawnRipple(
        Math.random(),
        Math.random(),
        true, // isBackground
        0.5 + Math.random() * 0.5 // depth
      );
    }
  }

  // ═══════════════════════════════════════════════
  // Ripple spawning
  // ═══════════════════════════════════════════════

  _allocRipple() {
    if (this._rippleFreeCount === 0) return -1;
    return this._rippleFreeList[--this._rippleFreeCount];
  }

  _freeRipple(index) {
    if (this._ripples[index].state === DEAD) return;
    this._ripples[index].state = DEAD;
    this._rippleFreeList[this._rippleFreeCount++] = index;
  }

  _spawnRipple(xFrac, yFrac, isBackground, depth) {
    const idx = this._allocRipple();
    if (idx === -1) return;

    const r = this._ripples[idx];
    r.state = ALIVE;
    r.x = xFrac;
    r.y = yFrac;
    r.radius = 0;
    r.maxRadius = isBackground ? (0.05 + Math.random() * 0.1) : (0.03 + Math.random() * 0.08);
    r.life = this.config.rippleLife * (0.7 + Math.random() * 0.6);
    r.maxLife = r.life;
    r.thickness = this.config.rippleThickness;
    r.alpha = isBackground ? 0.08 : 0.15;
    r.speed = isBackground ? (0.3 + Math.random() * 0.3) : (0.5 + Math.random() * 0.5);
    r.isBackground = isBackground;
    r.depth = depth;
  }

  // ═══════════════════════════════════════════════
  // Trail canvas (offscreen)
  // ═══════════════════════════════════════════════

  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  // ═══════════════════════════════════════════════
  // draw() override — trail fade technique
  // ═══════════════════════════════════════════════

  /**
   * Override draw() to produce trail fade that respects the background controller.
   *
   * Instead of overlaying a semi-transparent solid color (which ignores the global
   * background), we:
   * 1. Save the current canvas (previous frame) to an offscreen buffer
   * 2. Draw the proper background via clearCanvas() → drawBackground()
   * 3. Composite the saved frame on top at reduced opacity (trail effect)
   * 4. Render new content
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    // Ensure buffer is properly sized
    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    // Map frequency data to element count
    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    // Ensure previousAmplitudes is properly sized
    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    // Apply smoothing
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    // Store features for convenience getters (bassEnergy, beatDetected, etc.)
    this._currentFeatures = features;

    const { ctx, canvas } = this;

    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      // Trail effect via offscreen canvas compositing
      this._ensureTrailCanvas();

      // 1. Snapshot current canvas (previous frame) into offscreen buffer
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);

      // 2. Draw fresh background (respects background controller)
      this.clearCanvas();

      // 3. Composite previous frame on top at reduced opacity
      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    this.renderFrame(amplitudes, features);
  }

  // ═══════════════════════════════════════════════
  // Main renderFrame
  // ═══════════════════════════════════════════════

  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const dt = 1 / 60;
    this.time += dt;
    const w = canvas.width;
    const h = canvas.height;

    // Slow palette hue drift
    this._paletteHueOffset += dt * 0.05;

    // Compute band energies from amplitude array
    const quarter = Math.floor(amplitudes.length / 4);
    let subBassSum = 0, bassSum = 0, midSum = 0, highSum = 0;
    for (let i = 0; i < quarter; i++) subBassSum += amplitudes[i];
    for (let i = quarter; i < quarter * 2; i++) bassSum += amplitudes[i];
    for (let i = quarter * 2; i < quarter * 3; i++) midSum += amplitudes[i];
    for (let i = quarter * 3; i < amplitudes.length; i++) highSum += amplitudes[i];
    const subBassE = subBassSum / quarter;
    const bassE = bassSum / quarter;
    const midE = midSum / quarter;
    const highE = highSum / (amplitudes.length - quarter * 3);
    const overallE = (subBassE + bassE + midE + highE) / 4;

    this._bandEnergies[0] = subBassE;
    this._bandEnergies[1] = bassE;
    this._bandEnergies[2] = midE;
    this._bandEnergies[3] = highE;

    // Safe mode: reduce highlight ceiling
    const highlightCeil = config.safeMode
      ? Math.min(config.highlightCeiling, 0.5)
      : config.highlightCeiling;

    // Calmness factor: scales motion speed inversely
    const calmnessScale = config.calmness;

    // Energy rise detector — integrates energy changes for ripple cluster spawning
    const energyDelta = overallE - this._lastEnergy;
    this._lastEnergy = overallE;
    if (energyDelta > 0) {
      this._energyIntegrator += energyDelta * dt * 10;
    }
    this._energyIntegrator *= 0.95; // decay

    // Spawn ripple clusters on sustained energy rise
    if (this._energyIntegrator > this._energyThreshold) {
      this._energyIntegrator = 0;
      const clusterX = 0.2 + Math.random() * 0.6;
      const clusterY = 0.2 + Math.random() * 0.6;
      for (let i = 0; i < 3; i++) {
        this._spawnRipple(
          clusterX + (Math.random() - 0.5) * 0.1,
          clusterY + (Math.random() - 0.5) * 0.1,
          false,
          0.3 + Math.random() * 0.7
        );
      }
    }

    // Event ripples from mids
    if (midE > 0.3) {
      const rate = Math.floor(midE * config.eventRippleRate * dt * 60);
      for (let i = 0; i < rate; i++) {
        this._spawnRipple(
          0.1 + Math.random() * 0.8,
          0.1 + Math.random() * 0.8,
          false,
          0.2 + Math.random() * 0.8
        );
      }
    }

    // Respawn dead background ripples to maintain density
    let bgAlive = 0;
    for (let i = 0; i < MAX_RIPPLES; i++) {
      if (this._ripples[i].state === ALIVE && this._ripples[i].isBackground) bgAlive++;
    }
    const targetBg = config.baseRippleDensity;
    if (bgAlive < targetBg) {
      const toSpawn = Math.min(targetBg - bgAlive, 3);
      for (let i = 0; i < toSpawn; i++) {
        this._spawnRipple(Math.random(), Math.random(), true, 0.5 + Math.random() * 0.5);
      }
    }

    // ─── Layer 2: Water gradient + vignette ───
    this._drawWaterGradient(w, h, subBassE, highlightCeil);
    this._drawVignette(w, h);

    // ─── Layer 3: Caustics ───
    this._updateAndDrawCaustics(w, h, dt, bassE, calmnessScale, highlightCeil);

    // ─── Layer 4: Ripple field ───
    this._updateAndDrawRipples(w, h, dt, bassE, calmnessScale, highlightCeil);

    // ─── Layer 5: Mist ───
    this._updateAndDrawMist(w, h, dt, midE, calmnessScale, highlightCeil);

    // ─── Layer 6: Petals ───
    this._updateAndDrawPetals(w, h, dt, midE, calmnessScale, highlightCeil);

    // ─── Layer 7: Reflection specks ───
    this._updateAndDrawReflections(w, h, dt, highE, calmnessScale, highlightCeil);
  }

  // ═══════════════════════════════════════════════
  // Layer 2: Water gradient + vignette
  // ═══════════════════════════════════════════════

  _drawWaterGradient(w, h, subBassE, highlightCeil) {
    const { ctx, config } = this;
    const cx = w / 2;
    const cy = h / 2;

    // Radial gradient with slow bass-modulated color shift
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.7);

    const baseAlpha = Math.min(highlightCeil, 0.12 + subBassE * 0.08);
    const t1 = (Math.sin(this.time * 0.3) * 0.5 + 0.5);
    const t2 = (Math.cos(this.time * 0.2) * 0.5 + 0.5);

    grad.addColorStop(0, this._lerpMainColor(t1, baseAlpha));
    grad.addColorStop(0.5, this._getPaletteColor(t2, baseAlpha * 0.7));
    grad.addColorStop(1, this._lerpMainColor(1 - t1, baseAlpha * 0.4));

    ctx.save();
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  _drawVignette(w, h) {
    const { ctx } = this;

    if (this._vigW !== w || this._vigH !== h || !this._vignetteGrad) {
      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.max(w, h) * 0.7;
      this._vignetteGrad = ctx.createRadialGradient(cx, cy, radius * 0.25, cx, cy, radius);
      this._vignetteGrad.addColorStop(0, 'rgba(0,0,0,0)');
      this._vignetteGrad.addColorStop(1, 'rgba(0,0,0,1)');
      this._vigW = w;
      this._vigH = h;
    }

    ctx.save();
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = this._vignetteGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Layer 3: Caustics
  // ═══════════════════════════════════════════════

  _updateAndDrawCaustics(w, h, dt, bassE, calmnessScale, highlightCeil) {
    const { ctx, config } = this;
    const count = Math.min(config.causticPatchCount, MAX_CAUSTIC_PATCHES);

    ctx.save();

    for (let i = 0; i < count; i++) {
      const c = this._caustics[i];
      if (c.state === DEAD) continue;

      // Morph control points slowly
      c.morphTimer += dt * config.causticMorphSpeed * (1 - calmnessScale * 0.5);
      if (c.morphTimer >= c.morphInterval) {
        c.morphTimer = 0;
        c.morphInterval = 2 + Math.random() * 4;
        // Set new targets
        for (let j = 0; j < c.numPts; j++) {
          const baseAngle = (j / c.numPts) * TAU;
          c.targetAngles[j] = baseAngle + (Math.random() - 0.5) * 0.6;
          c.targetRadii[j] = 0.6 + Math.random() * 0.8;
        }
      }

      // Lerp toward targets
      const morphT = Math.min(1, c.morphTimer / c.morphInterval);
      const lerpFactor = morphT * morphT * (3 - 2 * morphT); // smoothstep

      // Parallax drift
      const drift = (1 - c.depth) * 0.3;
      const px = c.cx * w + Math.sin(this.time * 0.1 + c.phase) * w * 0.02 * drift;
      const py = c.cy * h + Math.cos(this.time * 0.08 + c.phase) * h * 0.02 * drift;
      const pRadius = c.radius * Math.min(w, h);

      // Alpha modulated by bass and contrast config
      const alpha = Math.min(highlightCeil, c.alpha * (1 + bassE * config.causticContrast * 2));

      ctx.globalAlpha = alpha;
      ctx.fillStyle = this._getPaletteColor(c.hueT + this.time * 0.02, 1);

      ctx.beginPath();
      for (let j = 0; j < c.numPts; j++) {
        const a = c.angles[j] + (c.targetAngles[j] - c.angles[j]) * lerpFactor + c.phase + this.time * 0.1;
        const r = (c.radii[j] + (c.targetRadii[j] - c.radii[j]) * lerpFactor) * pRadius;
        const ptX = px + Math.cos(a) * r;
        const ptY = py + Math.sin(a) * r;
        if (j === 0) {
          ctx.moveTo(ptX, ptY);
        } else {
          // Use quadratic curve to the midpoint between adjacent points for smooth shape
          const prevA = c.angles[j - 1] + (c.targetAngles[j - 1] - c.angles[j - 1]) * lerpFactor + c.phase + this.time * 0.1;
          const prevR = (c.radii[j - 1] + (c.targetRadii[j - 1] - c.radii[j - 1]) * lerpFactor) * pRadius;
          const prevX = px + Math.cos(prevA) * prevR;
          const prevY = py + Math.sin(prevA) * prevR;
          const mx = (prevX + ptX) / 2;
          const my = (prevY + ptY) / 2;
          ctx.quadraticCurveTo(prevX, prevY, mx, my);
        }
      }
      ctx.closePath();
      ctx.fill();

      // Additive highlight pass for volumetric illusion
      if (config.glowIntensity > 0) {
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = alpha * 0.3;
        ctx.shadowBlur = config.glowIntensity * 0.5;
        ctx.shadowColor = this._getPaletteColor(c.hueT, 0.8);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalCompositeOperation = 'source-over';
      }
    }

    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Layer 4: Ripples
  // ═══════════════════════════════════════════════

  _updateAndDrawRipples(w, h, dt, bassE, calmnessScale, highlightCeil) {
    const { ctx, config } = this;

    ctx.save();
    ctx.lineWidth = config.rippleThickness;

    for (let i = 0; i < MAX_RIPPLES; i++) {
      const r = this._ripples[i];
      if (r.state === DEAD) continue;

      // Update life
      r.life -= 1;
      if (r.life <= 0) {
        this._freeRipple(i);
        continue;
      }

      // Expand radius (slower with higher calmness)
      const speedMod = 1 - calmnessScale * 0.5;
      r.radius += r.speed * dt * speedMod;

      // Bass gently increases amplitude (ring size variation)
      const bassAmp = 1 + bassE * 0.3;

      // Life fraction for fading
      const lifeFrac = r.life / r.maxLife;
      const fadeAlpha = lifeFrac * (1 - lifeFrac) * 4; // bell curve: peaks at 0.5 life

      // Parallax scale
      const parallaxScale = 0.7 + r.depth * 0.3;

      const cx = r.x * w;
      const cy = r.y * h;
      const radius = r.radius * Math.min(w, h) * bassAmp * parallaxScale;

      const alpha = Math.min(highlightCeil, r.alpha * fadeAlpha);
      if (alpha < 0.005) continue;

      ctx.globalAlpha = alpha;
      ctx.strokeStyle = this._lerpMainColor(r.depth, 1);

      // Draw concentric rings (2-3 per ripple for richness)
      const rings = r.isBackground ? 2 : 3;
      for (let ring = 0; ring < rings; ring++) {
        const ringRadius = radius * (1 - ring * 0.15);
        if (ringRadius > 0) {
          ctx.beginPath();
          ctx.arc(cx, cy, ringRadius, 0, TAU);
          ctx.stroke();
        }
      }
    }

    ctx.restore();
  }

  // ═══════════════════════════════════════════════
  // Layer 5: Mist
  // ═══════════════════════════════════════════════

  _updateAndDrawMist(w, h, dt, midE, calmnessScale, highlightCeil) {
    const { ctx, config } = this;

    // Spawn mist blobs periodically
    this._mistSpawnTimer += dt;
    const spawnInterval = 2.0 / Math.max(1, config.mistDensity * 0.3);
    if (this._mistSpawnTimer >= spawnInterval) {
      this._mistSpawnTimer = 0;
      this._spawnMistBlob(w, h);
    }

    ctx.save();

    for (let i = 0; i < MAX_MIST_BLOBS; i++) {
      const m = this._mistBlobs[i];
      if (m.state === DEAD) continue;

      m.life -= dt;
      if (m.life <= 0) {
        m.state = DEAD;
        continue;
      }

      // Drift horizontally
      m.x += m.vx * dt * (1 - calmnessScale * 0.3);

      // Parallax: near mist drifts faster
      const parallaxDrift = m.depth * 0.5;
      m.x += parallaxDrift * dt * 3;

      // Fade in/out
      const lifeFrac = m.life / m.maxLife;
      const fadeEnvelope = Math.min(lifeFrac * 4, 1) * Math.min((1 - lifeFrac) * 4, 1);
      const alpha = Math.min(highlightCeil, m.maxAlpha * fadeEnvelope * (0.7 + midE * 0.3));

      if (alpha < 0.005) continue;

      // Near-top placement for mist (top 25% of canvas)
      const mx = m.x * w;
      const my = m.y * h;
      const mw = m.width * w;
      const mh = m.height * h;

      // Radial gradient for soft fog
      const grad = ctx.createRadialGradient(mx, my, 0, mx, my, mw * 0.5);
      const fogColor = this._getPaletteColor(m.depth * 0.5 + this.time * 0.01, alpha);
      grad.addColorStop(0, fogColor);
      grad.addColorStop(1, 'rgba(0,0,0,0)');

      ctx.globalAlpha = 1;
      ctx.fillStyle = grad;
      ctx.fillRect(mx - mw * 0.5, my - mh * 0.5, mw, mh);
    }

    ctx.restore();
  }

  _spawnMistBlob(w, h) {
    const m = this._mistBlobs[this._mistNextIdx];
    this._mistNextIdx = (this._mistNextIdx + 1) % MAX_MIST_BLOBS;

    m.state = ALIVE;
    m.x = -0.1 + Math.random() * 0.2; // start from left or center-left
    m.y = 0.05 + Math.random() * 0.2;  // top 25%
    m.width = 0.15 + Math.random() * 0.25;
    m.height = 0.05 + Math.random() * 0.1;
    m.vx = 0.01 + Math.random() * 0.03;
    m.maxAlpha = 0.04 + Math.random() * 0.06;
    m.life = 8 + Math.random() * 12;
    m.maxLife = m.life;
    m.depth = 0.6 + Math.random() * 0.4; // near-plane
  }

  // ═══════════════════════════════════════════════
  // Layer 6: Petals / leaves
  // ═══════════════════════════════════════════════

  _updateAndDrawPetals(w, h, dt, midE, calmnessScale, highlightCeil) {
    const { ctx, config } = this;

    // Spawn petals based on midE and config
    const targetPetals = config.petalCount;
    let activePetals = 0;
    for (let i = 0; i < MAX_PETALS; i++) {
      if (this._petals[i].state === ALIVE) activePetals++;
    }

    const spawnRate = Math.max(0, targetPetals - activePetals);
    const spawnPerFrame = Math.min(spawnRate, Math.ceil(dt * 60 * 0.5));
    for (let s = 0; s < spawnPerFrame; s++) {
      this._spawnPetal(w, h, midE);
    }

    ctx.save();

    for (let i = 0; i < MAX_PETALS; i++) {
      const p = this._petals[i];
      if (p.state === DEAD) continue;

      p.life -= dt;
      if (p.life <= 0) {
        p.state = DEAD;
        continue;
      }

      // Drift with calmness modulation
      const driftMod = config.driftSpeed * (1 - calmnessScale * 0.3) * (0.8 + midE * 0.4);
      p.x += p.vx * dt * driftMod;
      p.y += p.vy * dt * driftMod;
      p.rotation += p.rotationSpeed * dt * (1 - calmnessScale * 0.5);

      // Gentle sinusoidal wobble
      p.x += Math.sin(this.time * 0.5 + p.hueT * TAU) * dt * 0.003;
      p.y += Math.cos(this.time * 0.3 + p.hueT * TAU) * dt * 0.002;

      // Wrap around
      if (p.x > 1.1) p.x = -0.1;
      if (p.x < -0.15) p.x = 1.1;
      if (p.y > 1.1) p.y = -0.1;

      // Fade envelope
      const lifeFrac = p.life / p.maxLife;
      const fadeEnvelope = Math.min(lifeFrac * 3, 1) * Math.min((1 - lifeFrac) * 3, 1);
      const alpha = Math.min(highlightCeil, p.alpha * fadeEnvelope);

      if (alpha < 0.005) continue;

      const px = p.x * w;
      const py = p.y * h;
      const size = p.size * config.petalSize;

      // Parallax scale
      const parallaxScale = 0.6 + p.depth * 0.4;

      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(p.rotation);
      ctx.scale(parallaxScale, parallaxScale);
      ctx.globalAlpha = alpha;

      // Draw petal shape based on type
      ctx.fillStyle = this._getPaletteColor(p.hueT, 1);

      if (p.petalType === 0) {
        // Round petal
        ctx.beginPath();
        ctx.ellipse(0, 0, size, size * 0.6, 0, 0, TAU);
        ctx.fill();
      } else if (p.petalType === 1) {
        // Elongated petal with curve
        ctx.beginPath();
        ctx.moveTo(0, -size);
        ctx.quadraticCurveTo(size * 0.7, -size * 0.3, size * 0.3, size * 0.5);
        ctx.quadraticCurveTo(0, size, -size * 0.3, size * 0.5);
        ctx.quadraticCurveTo(-size * 0.7, -size * 0.3, 0, -size);
        ctx.fill();
      } else {
        // Leaf shape
        ctx.beginPath();
        ctx.moveTo(0, -size * 1.2);
        ctx.quadraticCurveTo(size * 0.5, 0, 0, size * 0.8);
        ctx.quadraticCurveTo(-size * 0.5, 0, 0, -size * 1.2);
        ctx.fill();
        // Vein line
        ctx.strokeStyle = this._getPaletteColor(p.hueT + 0.1, 0.3);
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(0, -size * 1.0);
        ctx.lineTo(0, size * 0.6);
        ctx.stroke();
      }

      ctx.restore();
    }

    ctx.restore();
  }

  _spawnPetal(w, h, midE) {
    const p = this._petals[this._petalNextIdx];
    this._petalNextIdx = (this._petalNextIdx + 1) % MAX_PETALS;

    p.state = ALIVE;
    p.x = Math.random();
    p.y = -0.05 - Math.random() * 0.1; // start above canvas
    p.rotation = Math.random() * TAU;
    p.rotationSpeed = (Math.random() - 0.5) * 0.3;
    p.vx = (Math.random() - 0.5) * 0.015;
    p.vy = 0.008 + Math.random() * 0.012; // gentle downward drift
    p.size = 0.6 + Math.random() * 0.8;
    p.life = 15 + Math.random() * 20;
    p.maxLife = p.life;
    p.alpha = 0.15 + Math.random() * 0.2;
    p.hueT = Math.random();
    p.depth = Math.random();
    p.petalType = Math.floor(Math.random() * 3);
  }

  // ═══════════════════════════════════════════════
  // Layer 7: Reflection specks
  // ═══════════════════════════════════════════════

  _updateAndDrawReflections(w, h, dt, highE, calmnessScale, highlightCeil) {
    const { ctx, config } = this;
    const targetCount = config.reflectionCount;

    // Spawn reflections to maintain density
    let activeCount = 0;
    for (let i = 0; i < MAX_REFLECTIONS; i++) {
      if (this._reflections[i].state === ALIVE) activeCount++;
    }

    // Treble controls density
    const desiredCount = Math.floor(targetCount * (0.3 + highE * 0.7));
    const toSpawn = Math.min(desiredCount - activeCount, Math.ceil(dt * 60 * 5));
    for (let s = 0; s < toSpawn; s++) {
      this._spawnReflection(w, h);
    }

    ctx.save();

    for (let i = 0; i < MAX_REFLECTIONS; i++) {
      const r = this._reflections[i];
      if (r.state === DEAD) continue;

      // Twinkle phase advance
      r.twinklePhase += r.twinkleSpeed * dt * (1 - calmnessScale * 0.3);

      // Twinkle alpha modulation
      const twinkle = (Math.sin(r.twinklePhase) * 0.5 + 0.5);
      const alpha = Math.min(
        highlightCeil,
        r.alpha * twinkle * config.twinkleStrength * (0.5 + highE * 0.5)
      );

      if (alpha < 0.003) {
        // Kill very dim reflections to allow new ones
        if (Math.random() < dt * 0.5) {
          r.state = DEAD;
        }
        continue;
      }

      const px = r.x * w;
      const py = r.y * h;
      const size = r.size * (0.7 + r.depth * 0.3);

      // Color: mostly white/pale with palette tint
      ctx.globalAlpha = alpha;
      ctx.fillStyle = this._getPaletteColor(r.hueT + this.time * 0.005, 1);

      ctx.beginPath();
      ctx.arc(px, py, size, 0, TAU);
      ctx.fill();

      // Bright center dot (highlight)
      if (alpha > 0.1 && config.glowIntensity > 0) {
        ctx.globalAlpha = alpha * 0.5;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(px, py, size * 0.4, 0, TAU);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  _spawnReflection(w, h) {
    const r = this._reflections[this._reflectionNextIdx];
    this._reflectionNextIdx = (this._reflectionNextIdx + 1) % MAX_REFLECTIONS;

    r.state = ALIVE;
    r.x = Math.random();
    // Aligned in horizontal bands (reflection streaks on water)
    r.bandY = Math.floor(Math.random() * 8) / 8;
    r.y = r.bandY + (Math.random() - 0.5) * 0.06;
    r.size = 0.5 + Math.random() * 1.5;
    r.alpha = 0.1 + Math.random() * 0.2;
    r.twinklePhase = Math.random() * TAU;
    r.twinkleSpeed = 1 + Math.random() * 3;
    r.depth = Math.random();
    r.hueT = Math.random();
  }

  // ═══════════════════════════════════════════════
  // Clear / cleanup
  // ═══════════════════════════════════════════════

  clear() {
    super.clear();
    this.time = 0;
    this._energyIntegrator = 0;
    this._lastEnergy = 0;
    this._paletteHueOffset = 0;
    this.isFirstFrame = true;

    // Reset all pools
    this._rippleFreeCount = MAX_RIPPLES;
    for (let i = 0; i < MAX_RIPPLES; i++) {
      this._ripples[i].state = DEAD;
      this._rippleFreeList[i] = i;
    }

    for (let i = 0; i < MAX_CAUSTIC_PATCHES; i++) {
      if (this._caustics[i]) this._caustics[i].morphTimer = 0;
    }

    for (let i = 0; i < MAX_PETALS; i++) {
      this._petals[i].state = DEAD;
    }
    this._petalNextIdx = 0;

    for (let i = 0; i < MAX_MIST_BLOBS; i++) {
      this._mistBlobs[i].state = DEAD;
    }
    this._mistNextIdx = 0;
    this._mistSpawnTimer = 0;

    for (let i = 0; i < MAX_REFLECTIONS; i++) {
      this._reflections[i].state = DEAD;
    }
    this._reflectionNextIdx = 0;

    // Clear trail buffer
    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }

    // Invalidate vignette cache
    this._vignetteGrad = null;

    // Re-spawn background ripples
    this._spawnBackgroundRipples();
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(LuxPond);

export { LuxPond };
export default LuxPond;
